/**
 * src/context/AuthContext.js
 * Provides authentication state and actions to the entire app.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../api/client';
import { setSentryUser } from '../lib/sentry';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('pt_user');
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed) setSentryUser(parsed);
      return parsed;
    } catch {
      return null;
    }
  });

  const login = useCallback(async (email, password) => {
    const response = await api.post('/portal/auth/login', { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('pt_token', token);
    localStorage.setItem('pt_user', JSON.stringify(userData));
    setUser(userData);
    setSentryUser(userData);
    return userData;
  }, []);

  const signup = useCallback(async (organizationName, email, password) => {
    const response = await api.post('/portal/auth/signup', { organizationName, email, password });
    const { token, user: userData, apiKeys } = response.data;
    localStorage.setItem('pt_token', token);
    localStorage.setItem('pt_user', JSON.stringify(userData));
    setUser(userData);
    setSentryUser(userData);
    return apiKeys;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('pt_token');
    localStorage.removeItem('pt_user');
    setUser(null);
    setSentryUser(null);
  }, []);

  /**
   * Mark the current user as email-verified in local state.
   * Called after the verify-email API call succeeds.
   */
  const verifyEmail = useCallback(() => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, emailVerified: true };
      localStorage.setItem('pt_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  /**
   * Request a new verification email for the current user.
   */
  const resendVerification = useCallback(async () => {
    const response = await api.post('/portal/auth/resend-verification');
    return response.data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, verifyEmail, resendVerification }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
